// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { bootstrapAuth, fetchMe, loginUser, signupUser, logoutUser, setAccess } from "../services/api";

const AuthContext = createContext(null);

// ✅ named export
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

// ✅ named export
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const ok = await bootstrapAuth();
        if (!ok) { setUser(null); return; }
        const me = await fetchMe();
        setUser(me);
      } catch {
        setAccess(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loginWithCredentials = async (email, password) => {
    const u = await loginUser(email, password);
    setUser(u);
    return u;
  };

  const signupAndLogin = async (email, password, settings = {}) => {
    const u = await signupUser(email, password, settings);
    setUser(u);
    return u;
  };

  const logout = async () => {
    try { await logoutUser(); } finally { setUser(null); }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    // keep manual setter if you use it elsewhere
    login: (u) => setUser(u),
    loginWithCredentials,
    signupAndLogin,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// (optional) default export if you prefer: export default AuthProvider;
